import React, { useState, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  Box,
  Typography,
  Popover,
  Paper,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isBrandedPreset } from '../theme';
import {
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarMonthIcon,
  ShoppingCart,
  ShoppingBag,
  People,
  Inventory,
  VerifiedUser,
  AdminPanelSettings,
  LocalShipping,
  Assignment
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useHrPendingNotifications } from '../hooks/useHrPendingNotifications';
import { useDocumentTabs } from '../context/DocumentTabsContext';
import { BrandLogo } from '../branding/BrandLogo';
import CompanyWebsiteLink from './CompanyWebsiteLink';

import APP_VERSION from '../version';

const drawerWidth = 240;

const Sidebar = ({ open, onClose }) => {
  const theme = useTheme();
  const modern = isBrandedPreset(theme.fw?.preset);
  const FW = theme.fw?.tokens || {};
  const lightNav = Boolean(theme.fw?.lightNav) && modern;
  const location = useLocation();
  const { tryNavigate } = useDocumentTabs();
  const { isAdmin, isManagerOrAdmin, user } = useAuth();
  const { pendingReceiptCount, pendingTimeOffCount, unreadMessageCount } = useHrPendingNotifications();
  const [submenuAnchor, setSubmenuAnchor] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const hoverCloseTimer = useRef(null);

  const clearHoverCloseTimer = () => {
    if (hoverCloseTimer.current) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  };

  const scheduleHoverClose = () => {
    clearHoverCloseTimer();
    hoverCloseTimer.current = setTimeout(() => {
      setSubmenuAnchor(null);
      setActiveSubmenu(null);
      hoverCloseTimer.current = null;
    }, 300);
  };

  const baseMenuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/',
      submenu: null,
    },
    {
      text: 'Schedule',
      icon: <CalendarMonthIcon />,
      path: '/schedule',
      submenu: null,
    },
    {
      text: 'Sales (CRM)',
      icon: <ShoppingCart />,
      path: null,
      submenu: [
        { text: 'Leads', path: '/leads' },
        { text: 'Quote Requests', path: '/quote-requests' },
        { text: 'Quotes', path: '/quotes' },
        { text: 'Material takeoffs', path: '/material-takeoffs' },
        { text: 'Orders', path: '/work-orders' },
        { text: 'Shipping', path: '/packing-slips' },
        { text: 'Invoices', path: '/customer-invoices' },
        { text: 'Payments', path: '/customer-payments' },
        { text: 'Order Calendar', path: '/work-orders/calendar' },
        { text: 'AR Aging', path: '/reports/ar-aging' },
        { text: 'Sales Summary', path: '/reports/sales-summary' },
        { text: 'Customers', path: '/customers' },
      ],
    },
    {
      text: 'Transportation',
      icon: <LocalShipping />,
      path: '/transportation',
      submenu: null,
    },
    {
      text: 'Shop Tasks',
      icon: <Assignment />,
      path: null,
      submenu: [
        { text: 'Tasks', path: '/shop-tasks', managerOnly: true },
        { text: 'Labor Allocation', path: '/shop-tasks/allocation', managerOnly: true },
        { text: 'Labor by Work Order', path: '/shop-tasks/labor-by-order', managerOnly: true },
        { text: 'Task Adoption', path: '/shop-tasks/adoption', managerOnly: true },
      ],
    },
    {
      text: 'Procurement',
      icon: <ShoppingBag />,
      path: null,
      submenu: [
        { text: 'P.O.s', path: '/procurement/po' },
        { text: 'Shipments Received', path: '/procurement/shipments-in' },
        { text: 'Vendor Invoices', path: '/procurement/invoices' },
        { text: 'Vendors', path: '/procurement/vendors' },
      ],
    },
    {
      text: 'Human Resources',
      icon: <People />,
      path: null,
      submenu: [
        { text: 'Employees', path: '/hr' },
        { text: 'Clocked In', path: '/hr/clocked-in', managerOnly: true },
        { text: 'Time Entries', path: '/hr/time-entries' },
        { text: 'Timecards', path: '/hr/timecards' },
        { text: 'Expense reimbursements',
          path: '/hr/expense-reimbursements',
          badge: pendingReceiptCount,
          managerOnly: true,
        },
        { text: 'Time off requests',
          path: '/hr/time-off-requests',
          badge: pendingTimeOffCount,
          managerOnly: true,
        },
        { text: 'Employee messages',
          path: '/hr/employee-messages',
          badge: unreadMessageCount,
          managerOnly: true,
        },
        { text: 'Training Documents', path: '/hr/training-documents' },
        { text: 'My Training', path: '/hr/my-training', managerOnly: true },
        { text: 'Training Sign-Offs', path: '/hr/training-signoffs', managerOnly: true },
        { text: 'Payroll Hours', path: '/reports/payroll-hours' },
        { text: 'Kiosk Mode', path: '/time-tracking/kiosk' },
      ],
    },
    {
      text: 'Inventory',
      icon: <Inventory />,
      path: '/inventory',
      submenu: [
        { text: 'Overview', path: '/inventory' },
        { text: 'Parts', path: '/parts' },
        { text: 'Consumables', path: '/consumables' },
        { text: 'Raw materials', path: '/raw-materials' },
        { text: 'Services', path: '/services' },
        { text: 'Products', path: '/products' },
      ],
    },
    {
      text: 'Quality',
      icon: <VerifiedUser />,
      path: '/quality',
      submenu: null,
    },
  ];

  // Add admin menu item only for admin users; filter HR items by role
  const menuItems = useMemo(() => {
    const estimatorAllowedPaths = new Set([
      '/quotes',
      '/work-orders',
      '/packing-slips',
      '/customers',
    ]);
    const items = baseMenuItems
      .filter(
        (item) =>
          !item.enterpriseAdminOnly || user?.role === 'enterprise_admin'
      )
      .map((item) => {
      if (!item.submenu) {
        // Estimators only use Sales (CRM) submenu items — hide standalone sections.
        if (user?.role === 'estimator') return null;
        return item;
      }
      let submenu = item.submenu.filter(
        (sub) => !sub.managerOnly || (isManagerOrAdmin && isManagerOrAdmin())
      );
      if (user?.role === 'estimator') {
        submenu = submenu.filter((sub) => estimatorAllowedPaths.has(sub.path));
      }
      return { ...item, submenu };
    })
      .filter(Boolean)
      .filter((item) => !item.submenu || item.submenu.length > 0);
    try {
      if (isAdmin && typeof isAdmin === 'function' && isAdmin()) {
        items.push({
          text: 'Admin',
          icon: <AdminPanelSettings />,
          path: '/admin',
          submenu: null,
        });
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
    return items;
  }, [isAdmin, isManagerOrAdmin, pendingReceiptCount, pendingTimeOffCount, unreadMessageCount, user?.role]);

  const handleNavigation = (path) => {
    if (path) {
      void tryNavigate(path);
      // Close sidebar on mobile after navigation
      if (window.innerWidth < 768) {
        onClose();
      }
    }
  };

  const handleSubmenuOpen = (event, item) => {
    clearHoverCloseTimer();
    setSubmenuAnchor(event.currentTarget);
    setActiveSubmenu(item.text);
  };

  const handleSubmenuClose = () => {
    setSubmenuAnchor(null);
    setActiveSubmenu(null);
    clearHoverCloseTimer();
  };

  const handleSubmenuMouseLeave = () => {
    scheduleHoverClose();
  };

  const handleSubmenuMouseEnter = () => {
    clearHoverCloseTimer();
  };

  const handleSubmenuItemClick = (path) => {
    handleNavigation(path);
    handleSubmenuClose();
  };

  const getActiveSubmenu = () => {
    return menuItems.find(item => item.text === activeSubmenu);
  };

  const isActive = (item) => {
    const normalize = (p) => String(p || '').replace(/\/+$/, '') || '/';
    const currentPath = normalize(location.pathname);
    const matchesPath = (candidate) => {
      const base = normalize(candidate);
      if (base === '/') return currentPath === '/';
      return currentPath === base || currentPath.startsWith(`${base}/`);
    };

    if (item.path) {
      return matchesPath(item.path);
    }
    if (item.submenu) {
      return item.submenu.some((sub) => matchesPath(sub.path));
    }
    return false;
  };

  return (
    <>
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          display: open ? 'block' : 'none',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            top: '48px',
            height: 'calc(100vh - 48px)',
            position: 'fixed',
            zIndex: 1200,
            ...(modern
              ? lightNav
                ? {
                    border: 'none',
                    borderRight: `1px solid ${FW.line}`,
                    backgroundColor: FW.white,
                    color: FW.ink,
                    backgroundImage: 'none',
                    boxShadow: 'inset -1px 0 0 rgba(0,0,0,0.04)',
                  }
                : {
                    border: 'none',
                    backgroundColor: FW.navy,
                    color: '#D7E3EF',
                    backgroundImage:
                      'linear-gradient(180deg, rgba(27,108,168,0.18) 0%, transparent 28%), radial-gradient(circle at top left, rgba(255,255,255,0.06), transparent 40%)',
                  }
              : {
                  borderRight: '2px solid #000',
                  backgroundColor: '#fff',
                }),
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', mt: modern ? 1.5 : 2, px: modern ? 1.25 : 1 }}>
            {modern && (
              <Typography
                sx={{
                  px: 1,
                  pb: 1,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: 1.4,
                  textTransform: 'uppercase',
                  color: lightNav ? FW.muted : 'rgba(215,227,239,0.55)',
                }}
              >
                Workspace
              </Typography>
            )}
            <List dense sx={{ display: 'flex', flexDirection: 'column', gap: modern ? 0.5 : 1 }}>
              {menuItems.map((item) => {
                const active = isActive(item);
                return (
                  <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                    <Box
                      onClick={item.submenu ? (e) => handleSubmenuOpen(e, item) : () => handleNavigation(item.path)}
                      onMouseEnter={item.submenu ? (e) => handleSubmenuOpen(e, item) : undefined}
                      onMouseLeave={item.submenu ? handleSubmenuMouseLeave : undefined}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        ...(modern
                          ? lightNav
                            ? {
                                borderRadius: 1.5,
                                border: `1px solid ${active ? FW.line : 'transparent'}`,
                                borderLeft: active
                                  ? `3px solid ${FW.accent || FW.black || FW.ink}`
                                  : '3px solid transparent',
                                backgroundColor: active ? FW.subtle : 'transparent',
                                color: active ? FW.ink : FW.muted,
                                py: 1,
                                px: 1.5,
                                '&:hover': {
                                  backgroundColor: FW.subtle,
                                  color: FW.ink,
                                },
                              }
                            : {
                                borderRadius: 1.5,
                                border: active
                                  ? '1px solid rgba(120,180,220,0.35)'
                                  : '1px solid transparent',
                                backgroundColor: active
                                  ? 'rgba(27,108,168,0.45)'
                                  : 'transparent',
                                color: active ? '#FFFFFF' : 'rgba(215,227,239,0.82)',
                                py: 1,
                                px: 1.5,
                                '&:hover': {
                                  backgroundColor: active
                                    ? 'rgba(27,108,168,0.55)'
                                    : 'rgba(255,255,255,0.06)',
                                },
                              }
                          : {
                              border: '2px solid #000',
                              borderRadius: 0,
                              backgroundColor: active ? '#1976d2' : '#fff',
                              color: active ? '#fff' : '#000',
                              py: 1.5,
                              px: 2,
                              textAlign: 'center',
                              '&:hover': {
                                backgroundColor: active ? '#1565c0' : '#f5f5f5',
                              },
                            }),
                      }}
                    >
                      <Typography
                        component="div"
                        variant="body1"
                        sx={{
                          fontWeight: modern ? (active ? 700 : 500) : 500,
                          fontSize: modern ? '0.9rem' : '0.95rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: modern ? 'flex-start' : 'center',
                          gap: modern ? 1.5 : 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            '& .MuiSvgIcon-root': { fontSize: modern ? '1.15rem' : undefined },
                          }}
                        >
                          {item.icon}
                        </Box>
                        {item.text}
                      </Typography>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Box>
          <Box
            onClick={() => handleNavigation('/')}
            sx={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2,
              px: 1,
              cursor: 'pointer',
              '&:hover': { opacity: 0.85 },
            }}
          >
            <BrandLogo
              slot="sidebar"
              plateOnDark={lightNav ? false : modern ? undefined : false}
              sx={{ width: '100%', justifyContent: 'center' }}
              imgSx={{
                maxWidth: '90%',
                width: '100%',
                height: 'auto',
              }}
            />
            <CompanyWebsiteLink slot="sidebar" sx={{ mt: 1.25, width: 'calc(100% - 8px)' }} />
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                color: modern ? (lightNav ? FW.muted : 'rgba(215,227,239,0.55)') : '#666',
                fontSize: '0.7rem',
              }}
            >
              v{APP_VERSION}
            </Typography>
          </Box>
        </Box>
      </Drawer>

      {/* Submenu Popover */}
      <Popover
        open={Boolean(submenuAnchor) && Boolean(activeSubmenu)}
        anchorEl={submenuAnchor}
        onClose={handleSubmenuClose}
        disableEnforceFocus
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
        sx={{
          pointerEvents: 'none',
          '& .MuiPaper-root': {
            pointerEvents: 'auto',
            mt: 1,
            ...(modern
              ? {
                  border: `1px solid ${FW.line}`,
                  borderRadius: 2,
                  boxShadow: '0 8px 24px rgba(16,32,48,0.14)',
                  overflow: 'hidden',
                }
              : {
                  border: '2px solid #000',
                  borderRadius: 0,
                  boxShadow: 'none',
                }),
          },
        }}
      >
        <Paper sx={{ p: 0, backgroundColor: '#fff' }} onMouseEnter={handleSubmenuMouseEnter} onMouseLeave={handleSubmenuMouseLeave}>
          {getActiveSubmenu()?.submenu?.map((subItem, index) => {
            const subActive =
              location.pathname === subItem.path ||
              (subItem.path !== '/' && location.pathname.startsWith(`${subItem.path}/`));
            return (
            <Box key={subItem.text}>
              <Box
                onClick={() => handleSubmenuItemClick(subItem.path)}
                sx={{
                  px: 3,
                  py: modern ? 1.25 : 1.5,
                  cursor: 'pointer',
                  ...(modern
                    ? {
                        backgroundColor: subActive
                          ? lightNav
                            ? FW.subtle
                            : FW.steelSoft
                          : '#fff',
                        borderBottom: `1px solid ${FW.line}`,
                        '&:last-of-type': { borderBottom: 'none' },
                        '&:hover': {
                          backgroundColor: lightNav ? FW.subtle : FW.steelSoft,
                        },
                      }
                    : {
                        backgroundColor: subActive ? '#e3f2fd' : '#fff',
                        borderBottom: '3px solid #1976d2',
                        '&:hover': { backgroundColor: '#e3f2fd' },
                      }),
                }}
              >
                <Typography
                  component="div"
                  variant="body2"
                  sx={{
                    fontWeight: modern && subActive ? 700 : 500,
                    fontSize: '0.9rem',
                    color: modern ? FW.ink : '#000',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <span>{subItem.text}</span>
                  {subItem.badge > 0 && (
                    <Chip
                      label={subItem.badge > 99 ? '99+' : subItem.badge}
                      size="small"
                      color="warning"
                      sx={{ height: 22, fontWeight: 700 }}
                    />
                  )}
                </Typography>
              </Box>
            </Box>
            );
          })}
        </Paper>
      </Popover>
    </>
  );
};

export default Sidebar;
